class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.handleEscapeKey());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.setupVariantEventDelegation();
    this.checkUrlParameter();
    this.confirmationDialog = null;
    this.isConfirmationShowing = false;

    this.lastCloseAt = 0;
    this.lastAddAt = 0;
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.lastAddAt = Date.now();
      });
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    //add to cart issue
    // Only capture the scroll position + apply the body scroll-lock when the
    // drawer isn't already open. open() gets called again for in-flight
    // re-renders (combo-offer popup, Koala freebie add/remove -> renderContents),
    // and by then the body is position:fixed so window.scrollY reads 0 — re-
    // capturing here would overwrite scrollPosition with 0 and jump the page to
    // the top on close. Guard on the lock class so the original position holds.
    const alreadyLocked = document.body.classList.contains('overflow-hidden');
    if (!alreadyLocked) {
      this.scrollPosition = window.scrollY || document.documentElement.scrollTop;
    }
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );
    if (!alreadyLocked) {
      document.body.style.top = `-${this.scrollPosition}px`;
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = scrollBarWidth > 0 ? `${scrollBarWidth}px` : '';
      document.body.classList.add('overflow-hidden');
    }
  }

  close() {
    this.lastCloseAt = Date.now();
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.style.top = '';
    document.body.style.paddingRight = '';
    window.scrollTo(0, this.scrollPosition || 0);
    document.dispatchEvent(new CustomEvent('cart-drawer:close'));
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    if (typeof CartOperationLock !== 'undefined') {
      CartOperationLock.syncCleanup();
    }

    document.dispatchEvent(new CustomEvent('cart:refresh'));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      // Only auto-open if the cart change that triggered this render is newer
      // than the user's last manual close. Prevents an in-flight async re-render
      // (combo-offer popup, bundle restore, slow section fetch) from reopening a
      // drawer the user already closed — the intermittent reopen-after-close bug.
      if (this.lastAddAt >= this.lastCloseAt) {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  setupVariantEventDelegation() {
    this.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"]') && event.target.closest('.drawer-product-card')) {
        this.handleDrawerVariantChange(event.target);
      }
    });
  }

  checkUrlParameter() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('open_cart_drawer') === 'true') {
      setTimeout(() => {
        this.open();
        const url = new URL(window.location);
        url.searchParams.delete('open_cart_drawer');
        window.history.replaceState({}, document.title, url);
      }, 500);
    }
  }

  handleDrawerVariantChange(radioInput) {
    const productCard = radioInput.closest('.drawer-product-card');
    if (!productCard) return;

    const variantSelects = productCard.querySelector('variant-selects');
    if (!variantSelects) return;

    const selectedOptions = [];
    variantSelects.querySelectorAll('input[type="radio"]:checked').forEach(function (input) {
      const nameParts = input.name.split('-');
      const position = parseInt(nameParts[nameParts.length - 2]) - 1;
      selectedOptions[position] = input.value;
    });

    const productId = productCard.dataset.productId;
    if (!productId) return;

    let variants = [];
    const variantsDataElement = variantSelects.querySelector('[data-product-variants]');
    if (variantsDataElement) {
      try {
        variants = JSON.parse(variantsDataElement.textContent);
      } catch (error) {
        console.error('Error parsing product variants data:', error);
      }
    }

    if (variants.length === 0) {
      const selectedVariantData = variantSelects.querySelector('[data-selected-variant]');
      if (selectedVariantData) {
        try {
          const selectedVariant = JSON.parse(selectedVariantData.textContent);
          if (selectedVariant && !Array.isArray(selectedVariant)) {
            variants = [selectedVariant];
          }
        } catch (error) {
          console.error('Error parsing selected variant data:', error);
        }
      }
    }

    if (variants.length === 0) return;

    const selectedVariant = variants.find(function (variant) {
      return (
        variant.options &&
        variant.options.every(function (option, index) {
          return option === selectedOptions[index];
        })
      );
    });

    if (selectedVariant) {
      const variantIdInput = productCard.querySelector('input[name="id"]');
      if (variantIdInput) variantIdInput.value = selectedVariant.id;

      const addToCartButton = productCard.querySelector('.quick-add__submit');
      if (addToCartButton) {
        addToCartButton.disabled = !selectedVariant.available;
        const buttonText = addToCartButton.querySelector('span');
        if (buttonText) buttonText.textContent = selectedVariant.available ? 'Add to cart' : 'Sold out';
      }

      this.updatePriceDisplayDirectly(productCard, selectedVariant);

      const selectedVariantScript = variantSelects.querySelector('[data-selected-variant]');
      if (selectedVariantScript) {
        selectedVariantScript.textContent = JSON.stringify(selectedVariant);
      }
    }
  }

  updatePriceDisplayDirectly(productCard, selectedVariant) {
    const priceDisplay = productCard.querySelector('.card-price');
    if (!priceDisplay || !selectedVariant) return;

    function formatMoney(cents) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      if (isNaN(cents) || cents == null) return 'Rs.0';
      const rupees = (cents / 100).toFixed(2);
      if (rupees.endsWith('.00')) return `Rs.${parseInt(rupees)}`;
      return `Rs.${rupees.replace(/\.?0+$/, '')}`;
    }

    const discountPercentage = productCard.dataset.discountPercentage || 0;
    let displayPrice = selectedVariant.price;
    let displayCompareAtPrice = selectedVariant.compare_at_price;

    if (selectedVariant.price > 100 && !selectedVariant.compare_at_price && discountPercentage > 0) {
      const discountAmount = selectedVariant.price * (discountPercentage / 100);
      displayPrice = selectedVariant.price - discountAmount;
      displayCompareAtPrice = selectedVariant.price;
    }

    if (!priceDisplay.classList.contains('price')) priceDisplay.classList.add('price');

    const priceContainer = priceDisplay.querySelector('.price__container');
    if (!priceContainer) return;

    const priceSaleSection = priceContainer.querySelector('.price__sale');
    const priceRegularSection = priceContainer.querySelector('.price__regular');

    if (displayCompareAtPrice && displayCompareAtPrice > displayPrice) {
      if (priceSaleSection) {
        priceSaleSection.style.display = 'block';
        const salePriceElement = priceSaleSection.querySelector('.price-item--sale');
        if (salePriceElement) {
          salePriceElement.textContent = formatMoney(displayPrice);
          salePriceElement.style.display = 'inline';
        }
        const strikethroughElement = priceSaleSection.querySelector('.variant-item__old-price');
        if (strikethroughElement) {
          strikethroughElement.textContent = formatMoney(displayCompareAtPrice);
          strikethroughElement.style.display = 'inline';
        }
      }
      if (priceRegularSection) priceRegularSection.style.display = 'none';
      priceDisplay.classList.add('price--on-sale');
    } else {
      if (priceSaleSection) priceSaleSection.style.display = 'none';
      if (priceRegularSection) {
        priceRegularSection.style.display = 'block';
        const regularPriceElement = priceRegularSection.querySelector('.price-item--regular');
        if (regularPriceElement) {
          regularPriceElement.textContent = formatMoney(displayPrice);
          regularPriceElement.style.display = 'inline';
        }
      }
      priceDisplay.classList.remove('price--on-sale');
    }
  }

  // ============= Cart Confirmation Dialog =============

  handleCloseAttempt() {
    // DISABLED: previously called the async checkAndShowConfirmation(), which
    // ran `await fetch('/cart.js')` before closing — causing a delay that made
    // the close button feel like it needed more than one click. Close directly.
    this.close();
    // this.checkAndShowConfirmation();
  }

  handleEscapeKey() {
    // DISABLED: close directly in one press (see handleCloseAttempt above).
    this.close();
    // if (this.isConfirmationShowing) {
    //   this.hideCloseConfirmation();
    // } else {
    //   this.checkAndShowConfirmation();
    // }
  }

  async checkAndShowConfirmation() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Identify restricted items from DOM data attributes
      const restrictedRows = document.querySelectorAll('tr.cart-item[data-restricted="true"]');
      const restrictedIds = new Set();
      restrictedRows.forEach((row) => {
        const removeBtn = row.querySelector('.cart-remove-button');
        if (removeBtn) restrictedIds.add(String(removeBtn.dataset.variantId));
      });

      // Calculate totals using only non-restricted items
      let nonRestrictedCount = 0;
      let nonRestrictedTotal = 0;
      cart.items.forEach((item) => {
        if (!restrictedIds.has(String(item.variant_id))) {
          nonRestrictedCount += item.quantity;
          nonRestrictedTotal += item.line_price;
        }
      });

      const cartTotal = nonRestrictedTotal / 100;
      const itemCount = nonRestrictedCount;

      // DISABLED: "lose exclusive discount" close-confirmation popup.
      // Previously this popup appeared when the cart had eligible quantity/total.
      // Now the drawer simply closes. To re-enable, uncomment the block below
      // and remove the `this.close();` line.
      this.close();

      // const eligible20 = itemCount >= 2 && cartTotal >= 500;
      // const eligible30 = itemCount >= 3 && cartTotal >= 500;

      // if (eligible30) {
      //   this.showCloseConfirmation({
      //     title: "You're about to lose exclusive discount !",
      //     message: "If you leave now, you'll miss it.",
      //     subtext: " ",
      //     type: 'lost-products-warning',
      //   });
      // } else if (eligible20) {
      //   this.showCloseConfirmation({
      //     title: "You're about to lose exclusive discount !",
      //     message: "If you leave now, you'll miss it.",
      //     subtext: " ",
      //     type: 'lost-products-warning',
      //   });
      // } else {
      //   this.close();
      // }
    } catch (error) {
      console.error('Error checking confirmation:', error);
      this.close();
    }
  }

  showCloseConfirmation(confirmationData) {
    this.confirmationDialog = this.querySelector('#cart-close-confirmation');
    if (!this.confirmationDialog) return;

    this.updateConfirmationContent(confirmationData);
    this.confirmationDialog.style.display = 'flex';
    this.isConfirmationShowing = true;

    setTimeout(() => {
      this.confirmationDialog.classList.add('show');
    }, 10);

    document.body.style.overflow = 'hidden';
    this.addConfirmationListeners();
  }

  updateConfirmationContent(confirmationData) {
    const titleElement = this.querySelector('.confirmation-title');
    const messageElement = this.querySelector('.confirmation-message');
    const subtitleElement = this.querySelector('.confirmation-subtitle');

    if (titleElement) titleElement.textContent = confirmationData.title || 'Wait!';
    if (subtitleElement) subtitleElement.textContent = confirmationData.subtext || '';
    if (messageElement) messageElement.textContent = confirmationData.message;
  }

  hideCloseConfirmation() {
    if (!this.confirmationDialog) return;

    this.confirmationDialog.classList.remove('show');
    this.isConfirmationShowing = false;

    setTimeout(() => {
      if (this.confirmationDialog) this.confirmationDialog.style.display = 'none';
    }, 300);

    document.body.style.overflow = '';
    this.removeConfirmationListeners();
  }

  forceClose() {
    this.hideCloseConfirmation();
    this.close();
  }

  addConfirmationListeners() {
    this.confirmationEscapeHandler = (e) => {
      if (e.code === 'Escape') {
        e.preventDefault();
        this.hideCloseConfirmation();
      }
    };

    this.confirmationOverlayHandler = (e) => {
      if (e.target === this.confirmationDialog.querySelector('.confirmation-overlay')) {
        this.hideCloseConfirmation();
      }
    };

    document.addEventListener('keydown', this.confirmationEscapeHandler);
    if (this.confirmationDialog) {
      this.confirmationDialog.addEventListener('click', this.confirmationOverlayHandler);
    }
  }

  removeConfirmationListeners() {
    if (this.confirmationEscapeHandler) {
      document.removeEventListener('keydown', this.confirmationEscapeHandler);
      this.confirmationEscapeHandler = null;
    }
    if (this.confirmationOverlayHandler && this.confirmationDialog) {
      this.confirmationDialog.removeEventListener('click', this.confirmationOverlayHandler);
      this.confirmationOverlayHandler = null;
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Microsoft Clarity tracking
document.addEventListener('DOMContentLoaded', function () {
  function waitForClarity(callback) {
    if (typeof clarity === 'function') {
      callback();
    } else {
      setTimeout(() => waitForClarity(callback), 300);
    }
  }

  waitForClarity(function () {
    document.addEventListener('cart-drawer:open', function () {
      clarity('set', 'page', '/cart');
      clarity('event', 'cart_drawer_open');
    });

    document.addEventListener('cart-drawer:close', function () {
      clarity('event', 'cart_drawer_close');
    });
  });
});
